import useIntl from '@/utils/intl'
import React, { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from 'react'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { validateRegno } from '@/services/mobi-api/confirmation'
import { useLanguage } from '@/context/LanguageContext'

import Loader from '@/components/ui/loader'

interface Props {
  instanceId?: string
  onContinue: (data: {
    regno: string
    choosenInfo?: { registerNumber: string }
  }) => void
}

const CYRILLIC_35 = [
  'А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ё', 'Ж', 'З', 'И', 'Й', 'К', 'Л', 'М', 'Н', 'О',
  'Ө', 'П', 'Р', 'С', 'Т', 'У', 'Ү', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ', 'Ы', 'Ь', 'Э', 'Ю', 'Я'
]
const CYR_SET = new Set(CYRILLIC_35)
type FocusTarget = 'l1' | 'l2' | 'digits' | null

const Step2: FunctionComponent<Props> = ({ onContinue, instanceId }) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()

  // regno state
  const [l1, setL1] = useState('')
  const [l2, setL2] = useState('')
  const [digits, setDigits] = useState('')
  const [focusTarget, setFocusTarget] = useState<FocusTarget>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [keyboardSpacer, setKeyboardSpacer] = useState(0)

  const [isValidating, setIsValidating] = useState(false)

  const l1Ref = useRef<HTMLInputElement | null>(null)
  const l2Ref = useRef<HTMLInputElement | null>(null)
  const digitsRef = useRef<HTMLInputElement | null>(null)
  const initialInnerHeightRef = useRef<number>(typeof window !== 'undefined' ? window.innerHeight : 0)

  const fullValue = `${l1}${l2}${digits}`
  const isComplete = l1 !== '' && l2 !== '' && /^\d{8}$/.test(digits)

  const getKeyboardInset = (forceFallback = false): number => {
    if (typeof window === 'undefined') return 0

    const viewport = window.visualViewport
    const baseHeight = initialInnerHeightRef.current > 0 ? initialInnerHeightRef.current : window.innerHeight
    const detectedInset =
      viewport != null
        ? baseHeight - viewport.height - viewport.offsetTop
        : 0
    const fallbackInset = forceFallback ? Math.round(baseHeight * 0.34) : 0

    return detectedInset > 120 ? detectedInset : fallbackInset
  }

  const updateKeyboardSpacer = (forceFallback = false): void => {
    if (typeof window === 'undefined') return

    const keyboardInset = getKeyboardInset(forceFallback)

    setKeyboardSpacer(keyboardInset > 0 ? keyboardInset + 32 : 0)
  }

  const findScrollParent = (el: HTMLElement): HTMLElement | null => {
    let parent = el.parentElement
    while (parent != null) {
      const overflowY = window.getComputedStyle(parent).overflowY
      if (/(auto|scroll|overlay)/.test(overflowY)) return parent
      parent = parent.parentElement
    }
    return null
  }

  const scrollInputIntoView = (el: HTMLInputElement | null): void => {
    if (el == null || typeof window === 'undefined') return

    const doScroll = (): void => {
      updateKeyboardSpacer(true)
      requestAnimationFrame(() => {
        const baseH = initialInnerHeightRef.current > 0 ? initialInnerHeightRef.current : window.innerHeight
        const vp = window.visualViewport
        const visibleHeight = vp != null && vp.height < baseH - 100
          ? vp.height
          : baseH - Math.round(baseH * 0.34)
        const rect = el.getBoundingClientRect()
        const targetBottom = visibleHeight - 16
        if (rect.bottom <= targetBottom) return
        const scrollAmount = rect.bottom - targetBottom
        const parent = findScrollParent(el)
        if (parent != null) {
          parent.scrollTop += scrollAmount
        } else {
          window.scrollBy(0, scrollAmount)
        }
      })
    }

    updateKeyboardSpacer(true)
    // 300ms: after iOS keyboard animation (~250ms); 500ms + 700ms: retry
    window.setTimeout(doScroll, 300)
    window.setTimeout(doScroll, 500)
    window.setTimeout(doScroll, 700)
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    initialInnerHeightRef.current = window.innerHeight

    const onResize = (): void => {
      const active = document.activeElement
      if (active === l1Ref.current || active === l2Ref.current || active === digitsRef.current) {
        scrollInputIntoView(active as HTMLInputElement)
      }
    }

    window.visualViewport?.addEventListener('resize', onResize)
    window.addEventListener('resize', onResize)
    return () => {
      window.visualViewport?.removeEventListener('resize', onResize)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const resetKeyboardSpacer = (event: React.FocusEvent<HTMLDivElement>): void => {
    const currentTarget = event.currentTarget
    const nextTarget = event.relatedTarget
    if (
      typeof Node !== 'undefined' &&
      nextTarget instanceof Node &&
      currentTarget.contains(nextTarget)
    ) {
      return
    }

    window.setTimeout(() => {
      if (typeof document === 'undefined') return
      const activeElement = document.activeElement
      if (
        typeof Node !== 'undefined' &&
        activeElement instanceof Node &&
        currentTarget.contains(activeElement)
      ) {
        return
      }

      setKeyboardSpacer(0)
    }, 100)
  }

  const focus = (t: FocusTarget): void => {
    setFocusTarget(t)
    const el =
      t === 'l1'
        ? l1Ref.current
        : t === 'l2'
          ? l2Ref.current
          : t === 'digits'
            ? digitsRef.current
            : null

    if (el != null) {
      el.focus({ preventScroll: true })
      scrollInputIntoView(el)
      if (t === 'digits') {
        const input = el
        const len = input.value?.length ?? 0
        input.setSelectionRange?.(len, len)
      }
    }
  }

  const toUpper = (s: string): string => s.toUpperCase()
  const isValidCyr = (ch: string): boolean => CYR_SET.has(toUpper(ch))

  const setLetterAt = (idx: 0 | 1, raw: string): void => {
    setErrorMessage('')
    const ch = toUpper(raw).slice(0, 1)
    if (ch === '' || !isValidCyr(ch)) {
      if (idx === 0) setL1('')
      else setL2('')
      return
    }

    if (idx === 0) {
      setL1(ch)
      focus('l2')
    } else {
      setL2(ch)
      focus('digits')
    }
  }

  const onLetterChange =
    (idx: 0 | 1) => (e: ChangeEvent<HTMLInputElement>) =>
      setLetterAt(idx, e.target.value)

  const onPickLetter = (ch: string): void => {
    if (focusTarget === 'l2') {
      setLetterAt(1, ch)
    } else {
      setLetterAt(0, ch)
    }
  }

  const sanitize8 = (raw: string): string => raw.replace(/\D/g, '').slice(0, 8)

  const onDigitsChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setErrorMessage('')
    setDigits(sanitize8(e.target.value))
  }

  const onDigitsPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault()
    setErrorMessage('')
    setDigits(sanitize8(e.clipboardData.getData('text')))
  }

  const onDigitsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
    if (allowed.includes(e.key)) return
    if (!/^\d$/.test(e.key)) e.preventDefault()
  }

  const getValidateErrorMessage = (
    res: Awaited<ReturnType<typeof validateRegno>>
  ): string => {
    const isMn = typeof locale === 'string' && locale.toLowerCase().startsWith('mn')

    if (isMn) {
      return (
        res?.data?.message?.mn ??
        res?.cause?.messageMn ??
        translate('verify.regnoError')
      )
    }

    return (
      res?.data?.message?.en ??
      res?.cause?.messageEn ??
      translate('verify.regnoError')
    )
  }

  const validateRegister = async (regno: string): Promise<void> => {
    try {
      const res = await validateRegno({ regno, instanceId })

      if ((res == null) || res.isValid !== true) {
        setErrorMessage(getValidateErrorMessage(res))
        setIsValidating(false)
        return
      }

      onContinue({ regno })
    } catch (error) {
      console.error(error)
      setErrorMessage(translate('verify.regnoError'))
    } finally {
      setIsValidating(false)
    }
  }

  const handleSubmit = async (): Promise<void> => {
    if (!isComplete || isValidating) return
    const regno = fullValue.toUpperCase()
    setErrorMessage('')
    setIsValidating(true)
    await validateRegister(regno)
  }

  return (
    <div
      className='w-full max-w-md mx-auto xs:p-8'
      onBlur={resetKeyboardSpacer}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: keyboardSpacer > 0 ? keyboardSpacer : 'env(safe-area-inset-bottom)'
      }}
    >
      <span className='text-textHigh font-extrabold text-lg'>
        {translate('verify.register.number')}
      </span>
      <div className='w-full overflow-hidden border border-secondarySoftLow rounded-xl bg-surfaceWebGrey p-3 sm:p-4 border-mb-20 pt-4'>
        <div className='grid grid-cols-[2.75rem_2.75rem_minmax(0,1fr)] sm:grid-cols-[3.5rem_3.5rem_minmax(0,1fr)] gap-2 pb-4 min-w-0 items-stretch'>
          <input
            name='L1'
            ref={l1Ref}
            type='text'
            inputMode='none'
            maxLength={1}
            value={l1}
            onFocus={() => {
              setFocusTarget('l1')
              scrollInputIntoView(l1Ref.current)
            }}
            onChange={onLetterChange(0)}
            placeholder='-'
            className={[
              'w-11 h-11 sm:w-14 sm:h-14 min-w-0 text-center border rounded-lg text-lg text-textHigh sm:text-xl font-semibold uppercase bg-surfaceWebGrey',
              'p-2 sm:p-4 placeholder-secondarySoftLow focus:placeholder-transparent',
              'focus:outline-none focus:ring-0',
              errorMessage !== ''
                ? 'border-red-500'
                : focusTarget === 'l1'
                  ? 'border-secondarySoftHigh'
                  : 'border-secondarySoftLow'
            ].join(' ')}
          />

          <input
            name='L2'
            ref={l2Ref}
            type='text'
            inputMode='none'
            maxLength={1}
            value={l2}
            onFocus={() => {
              setFocusTarget('l2')
              scrollInputIntoView(l2Ref.current)
            }}
            onChange={onLetterChange(1)}
            placeholder='-'
            className={[
              'w-11 h-11 sm:w-14 sm:h-14 min-w-0 text-center border rounded-lg text-lg text-textHigh sm:text-xl font-semibold uppercase bg-surfaceWebGrey',
              'p-2 sm:p-4 placeholder-secondarySoftLow focus:placeholder-transparent',
              'focus:outline-none focus:ring-0',
              errorMessage !== ''
                ? 'border-red-500'
                : focusTarget === 'l2'
                  ? 'border-secondarySoftHigh'
                  : 'border-secondarySoftLow'
            ].join(' ')}
          />

          <input
            name='Digits'
            ref={digitsRef}
            type='tel'
            inputMode='numeric'
            pattern='[0-9]*'
            maxLength={8}
            value={digits}
            onFocus={() => {
              setFocusTarget('digits')
              scrollInputIntoView(digitsRef.current)
            }}
            onChange={onDigitsChange}
            onPaste={onDigitsPaste}
            onKeyDown={onDigitsKeyDown}
            enterKeyHint='done'
            placeholder='12345678'
            className={[
              'w-full min-w-0 h-11 sm:h-14 text-center border rounded-lg text-[15px] sm:text-lg text-textHigh font-mono tracking-[0.04em] sm:tracking-[0.2em] bg-surfaceWebGrey',
              'px-2 sm:p-4 placeholder-secondarySoftLow focus:placeholder-transparent',
              'focus:outline-none focus:ring-0',
              errorMessage !== ''
                ? 'border-red-500'
                : focusTarget === 'digits'
                  ? 'border-secondarySoftHigh'
                  : 'border-secondarySoftLow'
            ].join(' ')}
          />
        </div>

        <DarkButton
          classNames='w-full mx-auto bg-surface text-textHigh'
          onClick={() => {
            void handleSubmit()
          }}
          disabled={!isComplete || isValidating}
        >
          {isValidating ? <Loader /> : translate('continue')}
        </DarkButton>
      </div>

      {errorMessage !== '' && (
        <p className='text-red-500 text-center text-sm mt-4 break-words '>
          {errorMessage}
        </p>
      )}

      {(focusTarget === 'l1' || focusTarget === 'l2') && (
        <div className='border border-surfaceLow bg-surfaceMid rounded-2xl p-2 mt-4 '>
          <div className='rounded-xl p-2 bg-surfaceLow'>
            <div>
              <div className='grid grid-cols-5 gap-2 w-full bg-surfaceWebGray'>
                {CYRILLIC_35.map((ch) => (
                  <button
                    key={ch}
                    type='button'
                    onClick={() => onPickLetter(ch)}
                    className='h-10 md:h-12 px-3 border rounded-lg bg-surfaceInverse active:scale-95 transition text-textHigh md:text-lg font-semibold'
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Step2
